import { z } from "zod";
import type { Severity } from "./event-envelope.js";
import { FormatPreferencesSchema } from "./format.js";

/**
 * The notifications engine (#564): something happened in an environment ->
 * one activity row (`NotificationEvents`) -> fan-out to the people who may
 * see it and want it -> one outbox row per person and channel
 * (`NotificationOutbox`) -> the dispatcher sends it through the one sender.
 *
 * Tier-2 metadata only (ADR-0024): names, numbers and times the portal
 * already shows, never anything a caller said.
 */

/** What happened. A closed vocabulary: a type is added here, with its writer, its message and its category. */
export const NotificationTypeSchema = z.enum([
  "alarm.opened",
  "alarm.resolved",
  "reporting.stopped",
  "reporting.resumed",
  // 4b, off by default (#564).
  "template.available",
  "quota.near_limit",
  "quota.request_status",
  "support_case.updated",
  "flow_check.finding",
  "change.waiting",
  "change.decided",
  "emergency_close.set",
  "emergency_close.ended",
  "config.not_pulled",
]);
export type NotificationType = z.infer<typeof NotificationTypeSchema>;

/** What a person chooses about. Every type maps to exactly one (`categoryOf`). */
export const NotificationCategorySchema = z.enum([
  "alarms-critical",
  "alarms-other",
  "stopped-reporting",
  "template-available",
  "quotas",
  "support-cases",
  "flow-check",
  "promotions",
  "emergency-close",
  "config-not-pulled",
]);
export type NotificationCategory = z.infer<typeof NotificationCategorySchema>;

export const NotificationChannelSchema = z.enum(["email"]);
export type NotificationChannel = z.infer<typeof NotificationChannelSchema>;

export function categoryOf(type: NotificationType, severity: Severity | undefined): NotificationCategory {
  switch (type) {
    case "alarm.opened":
    case "alarm.resolved":
      return severity === "critical" ? "alarms-critical" : "alarms-other";
    case "reporting.stopped":
    case "reporting.resumed":
      return "stopped-reporting";
    case "template.available":
      return "template-available";
    case "quota.near_limit":
    case "quota.request_status":
      return "quotas";
    case "support_case.updated":
      return "support-cases";
    case "flow_check.finding":
      return "flow-check";
    case "change.waiting":
    case "change.decided":
      return "promotions";
    case "emergency_close.set":
    case "emergency_close.ended":
      return "emergency-close";
    case "config.not_pulled":
      return "config-not-pulled";
  }
}

/**
 * The permission a person must hold on the event's environment to be told
 * about it: the one that lets them see the thing in the portal, resolved
 * per person and per environment at fan-out, as a request is. An event that
 * names a profile also needs that profile to be visible to them (ADR-0017 2b).
 */
export function permissionFor(type: NotificationType): string {
  switch (type) {
    case "alarm.opened":
    case "alarm.resolved":
    case "reporting.stopped":
    case "reporting.resumed":
      return "alarms:enabled";
    case "template.available":
      return "environment:enabled";
    case "quota.near_limit":
    case "quota.request_status":
      return "quotas:enabled";
    case "support_case.updated":
      return "support_cases:enabled";
    case "flow_check.finding":
      return "configuration:enabled";
    case "change.waiting":
      // Only the people who can decide a change are asked to.
      return "configuration.promotions:approve";
    case "change.decided":
      return "configuration.promotions:view";
    case "emergency_close.set":
    case "emergency_close.ended":
      return "configuration.overrides:view";
    case "config.not_pulled":
      return "configuration.profiles:view";
  }
}

/**
 * The one defaults table (crmarr/templates transactional-email-content,
 * Mermera #1079: a box the page shows ticked must be a box the sender sends
 * on). The Account page and the fan-out both read it; a person with no
 * stored choice gets exactly this. Gairm staff start with everything off -
 * the alarms Slack channel is theirs - and may opt in.
 */
export const NOTIFICATION_CATEGORIES: ReadonlyArray<{
  id: NotificationCategory;
  label: string;
  covers: string;
  defaults: { customer: Record<NotificationChannel, boolean>; staff: Record<NotificationChannel, boolean> };
}> = [
  {
    id: "stopped-reporting",
    label: "An environment stopped reporting",
    covers: "Starsach has not heard from an environment's stack for three hours, and when it is back.",
    defaults: { customer: { email: true }, staff: { email: false } },
  },
  {
    id: "alarms-critical",
    label: "Critical alarms",
    covers: "A critical CloudWatch alarm went into alarm, and when it is back to OK.",
    defaults: { customer: { email: true }, staff: { email: false } },
  },
  {
    id: "alarms-other",
    label: "Other alarms",
    covers: "A warning or informational alarm went into alarm, and when it is back to OK.",
    defaults: { customer: { email: false }, staff: { email: false } },
  },
  // 4b (#564): every one off by default, for customers and staff alike.
  {
    id: "template-available",
    label: "A new template version",
    covers: "A new version of the Starsach template is out and an environment's stack is behind it.",
    defaults: { customer: { email: false }, staff: { email: false } },
  },
  {
    id: "quotas",
    label: "Service quotas",
    covers: "A service quota reached 80% of its limit, and AWS changed the status of an increase request.",
    defaults: { customer: { email: false }, staff: { email: false } },
  },
  {
    id: "support-cases",
    label: "AWS Support cases",
    covers: "An AWS Support case in the environment's AWS account was updated.",
    defaults: { customer: { email: false }, staff: { email: false } },
  },
  {
    id: "flow-check",
    label: "Flow check findings",
    covers: "The flow check flagged a flow it had not flagged before.",
    defaults: { customer: { email: false }, staff: { email: false } },
  },
  {
    id: "promotions",
    label: "Changes waiting for approval",
    covers: "A change is waiting for someone who can approve it, and when it is approved or rejected.",
    defaults: { customer: { email: false }, staff: { email: false } },
  },
  {
    id: "emergency-close",
    label: "Emergency close",
    covers: "Someone set an emergency close on a profile, and when someone ends it.",
    defaults: { customer: { email: false }, staff: { email: false } },
  },
  {
    id: "config-not-pulled",
    label: "Configuration not pulled",
    covers: "The stack could not pull a saved configuration, so callers still get the last version that worked.",
    defaults: { customer: { email: false }, staff: { email: false } },
  },
];

/** A person's stored choices: only what they changed. Anything absent is the default above. */
export const NotificationPreferencesSchema = z.partialRecord(NotificationCategorySchema, z.object({ email: z.boolean() }).partial());
export type NotificationPreferences = z.infer<typeof NotificationPreferencesSchema>;

export function wantsNotification(prefs: NotificationPreferences | undefined, category: NotificationCategory, channel: NotificationChannel, staff: boolean): boolean {
  const chosen = prefs?.[category]?.[channel];
  if (chosen !== undefined) return chosen;
  const row = NOTIFICATION_CATEGORIES.find((c) => c.id === category)!;
  return row.defaults[staff ? "staff" : "customer"][channel];
}

/** `NotificationEvents`: one row per thing that happened. */
export const NotificationEventRecordSchema = z.object({
  organisation_id: z.string().min(1),
  /** `${occurred_at}#${event_id}` */
  sk: z.string().min(1),
  /** Deterministic from the source, so a redelivered source writes the row once (conditional put). */
  event_id: z.string().min(1),
  type: NotificationTypeSchema,
  environment_id: z.string().min(1),
  environment_name: z.string(),
  aws_account_number: z.string().optional(),
  severity: z.enum(["critical", "warning", "info"]).optional(),
  alarm_name: z.string().optional(),
  /** Connect's case number, display only (#525). */
  ticket: z.string().optional(),
  /** Stopped reporting: when the stack was last heard from. */
  last_heard_at: z.string().optional(),
  // 4b's display fields (#564): names, numbers, statuses and times the portal already shows, never text a person typed.
  /** The profile an event is about; the fan-out tells only people who may see it. */
  profile_id: z.string().optional(),
  profile_name: z.string().optional(),
  /** A quota's or a flow's name. */
  item_name: z.string().optional(),
  /** From a closed vocabulary: an increase request's status, a support case's status, a change's outcome. */
  status: z.string().optional(),
  /** A quota's use, in percent of its limit. */
  percent: z.number().optional(),
  /** How many things the event covers (flows newly flagged). */
  count: z.number().int().optional(),
  /** A template version, or a configuration version. */
  version: z.string().optional(),
  /** An AWS Support case's display id, the kind of change (`promotion`, `direct`, `rollback`, `binding`), or the template version a stack runs. */
  reference: z.string().optional(),
  /** An emergency close's window. */
  starts_at: z.string().optional(),
  ends_at: z.string().optional(),
  /** #650: the zone the event's times belong to, when not the reader's - an emergency close's schedule. An IANA id. */
  time_zone: z.string().optional(),
  occurred_at: z.string(),
  ttl: z.number().int(),
});
export type NotificationEventRecord = z.infer<typeof NotificationEventRecordSchema>;

/**
 * `sent`, or why not: a refusal is never recorded as sent (Mermera #959).
 * `skipped` is decided at fan-out or before the send (no address, stale);
 * `not_sent` is the sender's own refusal (allow-list, suppression).
 */
export const OutboxStatusSchema = z.enum(["pending", "sending", "sent", "skipped", "not_sent", "failed"]);
export type OutboxStatus = z.infer<typeof OutboxStatusSchema>;

/**
 * Where an outbox row goes: a person's email, or (#568) one of the
 * organization's Slack channels. Slack is never a person's choice, so it is
 * not a `NotificationChannel`: the organization routes it, per environment.
 */
export const OutboxChannelSchema = z.enum(["email", "slack"]);
export type OutboxChannel = z.infer<typeof OutboxChannelSchema>;

/** `NotificationOutbox`: one row per recipient and channel. */
export const OutboxRecordSchema = z.object({
  event_id: z.string().min(1),
  /** `${channel}#${user_id}`; for Slack, `slack#${connection_id}` - one post per event per channel, however many people hear of it. */
  sk: z.string().min(1),
  organisation_id: z.string().min(1),
  /** The `NotificationEvents` sort key, so the dispatcher can read the event. */
  event_sk: z.string().min(1),
  channel: OutboxChannelSchema,
  /** The recipient; for a Slack row there is no person, and this is the channel's connection id. */
  user_id: z.string().min(1),
  /** #568: a Slack row's channel, looked up under `organisation_id` and nowhere else. */
  connection_id: z.string().optional(),
  /** Where the recipient's own row lives: their organization, or `__internal__` for staff; a Slack row's is the event's organization. */
  user_organisation_id: z.string().min(1),
  address: z.string().optional(),
  /**
   * #650: the recipient's time zone, date format and clock as they stood at fan-out, so their copy reads like their
   * portal (`recipientFormat` fills in what is absent). Absent on a Slack row: a channel has no one reader.
   */
  format: FormatPreferencesSchema.partial().optional(),
  status: OutboxStatusSchema,
  reason: z.string().optional(),
  attempts: z.number().int(),
  created_at: z.string(),
  claimed_at: z.string().optional(),
  sent_at: z.string().optional(),
  ttl: z.number().int(),
});
export type OutboxRecord = z.infer<typeof OutboxRecordSchema>;

/**
 * #649: one-click unsubscribe (RFC 8058; crmarr/templates
 * transactional-email-content rule 15). Every notice in a category is
 * leavable, so each carries a signed link that turns off that one category's
 * email for that one person, with no sign-in. Account mail is outside every
 * category and never carries one.
 *
 * POST /v1/public/unsubscribe: the token from the link, and `email: true`
 * to undo (turn the category back on) from the confirmation page.
 */
export const UnsubscribeRequestSchema = z.object({ token: z.string().min(20).max(600), email: z.boolean().optional() });
export type UnsubscribeRequest = z.infer<typeof UnsubscribeRequestSchema>;
export interface UnsubscribeResponse {
  category: NotificationCategory;
  /** The category's label, as Account › Notifications shows it. */
  label: string;
  /** Whether that category's email is now on. */
  email: boolean;
  /** False when it already was as asked: nothing was written. */
  changed: boolean;
}
